//Library Imports
import { useState, useEffect } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { Radio, RadioChangeEvent, Spin } from "antd";

//Local Imports
import { RootState } from "store";
import AddAction from "./Modals/AddAction";
import Controlls from "./Controlls";
import DropAreaItems from "./DropAreaItems";
import AutomationDragLayer from "./AutomationDragLayer";
import ConfirmDialog from "modules/Common/Components/ConfirmDialog";
import {
  deleteActionModal,
  deleteConditionModal,
  deleteNode,
  getWorkflowDetails,
  resetWorkflow,
  conditionConstraintModal,
  publishWorkflow,
  setInactiveModal,
  updatedWorkflowAPI,
  setPublishModal,
  removeWFEmail,
  updateToasterMessage,
} from "../actions";
import { WFStatusEnum, ControlNameEnum } from "../interfaces";
import { inactivateModalText, publishWorkflowSuccess, saveWorkflowSuccess } from "../constants";
import DialogBox from "modules/Common/Components/DialogBox";
import { getQueryParam } from "utils";

const CreateNewAutomation = () => {
  // Redux States
  const deleteAction = useSelector((state: RootState) => state.automation.modals.deleteAction);
  const deleteCondition = useSelector((state: RootState) => state.automation.modals.deleteCondition);
  const conditionConstraint = useSelector((state: RootState) => state.automation.modals.conditionConstraint);
  const publishModal = useSelector((state: RootState) => state.automation.modals.publish);
  const inactiveModal = useSelector((state: RootState) => state.automation.modals.inactive);
  const deleteNodeObj = useSelector((state: RootState) => state.automation.workflow.toBeDeleteNode);
  const workflowId = useSelector((state: RootState) => state.automation.workflow.detail.id);
  const workflow = useSelector((state: RootState) => state.automation.workflow.flow);
  const loading: boolean = useSelector((state: RootState) => state.automation.workflow.loading);

  // Local States
  const dispatch = useDispatch();
  const workflowIdFromUrl = getQueryParam("workflow_id");
  const [radioValue, setRadioValue] = useState(0);
  const [dialogBoxKey, setDialogBoxKey] = useState("");

  const onChangeRadioValue = (e: RadioChangeEvent) => {
    setRadioValue(e.target.value);
  };

  // Component Logic
  useEffect(() => {
    if (!workflowId) {
      getWorkflowDetails(parseInt(workflowIdFromUrl));
    }

    return () => {
      dispatch(resetWorkflow());
    };
  }, []);

  useEffect(() => {
    if (conditionConstraint) {
      setDialogBoxKey("condition");
    } else if (deleteAction) {
      setDialogBoxKey("delete");
    } else if (publishModal) {
      setDialogBoxKey('publish');
    } else if (inactiveModal) {
      setDialogBoxKey('inactive');
    } else {
      setDialogBoxKey("");
    }
  }, [conditionConstraint, deleteAction, publishModal, inactiveModal]);

  const dialogBox: { [key: string]: any } = {
    publish: {
      isWarning: true,
      heading: "",
      isBodyVisible: false,
      bodyText: `Please ensure that the emails added to the workflow are
      ready to be send, as once the workflow is Published, you'll
      no longer be able to edit those emails.`,
      isModalOpen: publishModal,
      isCloseBtnVisible: true,
      closeHandler: (type: string) => {
        if (type === "cancel") {
          dispatch(setPublishModal(false));
        } else {
          // save without publishing
          dispatch(updateToasterMessage(saveWorkflowSuccess));
          publishWorkflow(true);
        }
      },
      proceedBtnText: "Publish",
      proceedHandler: () => {
        dispatch(updateToasterMessage(publishWorkflowSuccess));
        publishWorkflow();
      },
      closeBtnText: "Save and Publish later",
    },
    inactive: {
      isWarning: true,
      heading: "",
      isBodyVisible: false,
      bodyText: inactivateModalText,
      isModalOpen: inactiveModal,
      closeHandler: () => {
        dispatch(setInactiveModal(false));
      },
      closeBtnText: "No",
      isCloseBtnVisible: true,
      proceedBtnText: "Yes",
      proceedHandler: () => {
        updatedWorkflowAPI({
          workflowId,
          status: WFStatusEnum.INACTIVE
        });
        dispatch(setInactiveModal(false));
      },
    },
    condition: {
      heading: `Can't Perform the Action. `,
      isBodyVisible: true,
      bodyText: "Add Send Email control before condition.",
      isModalOpen: conditionConstraint,
      isCloseBtnVisible: false,
      closeHandler: () => {
        dispatch(conditionConstraintModal(false));
      },
      proceedBtnText: "OK",
      proceedHandler: () => {
        dispatch(conditionConstraintModal(false));
      },
    },
    // delete: {
    //   heading: "Are you sure?",
    //   isBodyVisible: false,
    //   bodyText: "",
    //   isModalOpen: deleteAction,
    //   isCloseBtnVisible: true,
    //   closeHandler: () => {
    //     dispatch(deleteActionModal(false, ""));
    //   },
    //   proceedBtnText: "Proceed",
    //   proceedHandler: () => {
    //     dispatch(deleteNode(deleteNodeObj.id, deleteNodeObj.index || 0));
    //     let node = workflow[deleteNodeObj.id];
    //     if (node?.detail?.controlName === ControlNameEnum.SEND_EMAIL) {
    //       dispatch(removeWFEmail(node.detail.emailId));
    //     }
    //   },
    // },
  };

  let dialogBoxObject = dialogBox[dialogBoxKey];
  return (
    <>
      <SpinWrapper hidden={!loading}>
        <Spin className="spin-loader" size="large" />
      </SpinWrapper>
      <Wrapper>
        <div className={loading ? "d-flex disabled" : "d-flex"}>
          <DropAreaItems />
          <div className="controll-panel">
            <Controlls />
          </div>
        </div>
        <AddAction />
        <DialogBox
          isWarning={false}
          clickOutsideClose={false}
          heading="Are you sure to delete it?"
          isBodyVisible
          isCenterAligned={false}
          bodyText="If you delete this if/else branch we need to know what you wish to do with all the actions that are
        within the if/else"
          isModalOpen={deleteCondition}
          closeHandler={() => {
            dispatch(deleteConditionModal(false, ""));
          }}
          proceedBtnText="Proceed"
          proceedHandler={() => {
            dispatch(deleteActionModal(true, deleteNodeObj.id, radioValue));
          }}
        >
          {/* Radio Buttons */}
          <div className="radio_group">
            <Radio.Group onChange={onChangeRadioValue} value={radioValue}>
              <Radio value={0} className="options">
                Keep only “YES” path
              </Radio>
              <Radio value={1} className="options">
                Keep only “No” path
              </Radio>
            </Radio.Group>
          </div>
        </DialogBox>
        {/* Added the following component because in the UI we have to open two modals at a time ie.e when deleting a conditional node*/}
        <DialogBox
          isWarning={dialogBoxObject?.isWarning}
          heading={dialogBoxObject?.heading}
          isBodyVisible={dialogBoxObject?.isBodyVisible}
          bodyText={dialogBoxObject?.bodyText}
          isModalOpen={dialogBoxObject?.isModalOpen}
          closeHandler={dialogBoxObject?.closeHandler}
          isCloseBtnVisible={dialogBoxObject?.isCloseBtnVisible}
          closeBtnText={dialogBoxObject?.closeBtnText}
          proceedBtnText={dialogBoxObject?.proceedBtnText}
          proceedHandler={dialogBoxObject?.proceedHandler}
        />
        {
          deleteAction && (
            <ConfirmDialog
              cancel="Cancel"
              confirm="Yes"
              onConfirm={() => {
                dispatch(deleteNode(deleteNodeObj.id, deleteNodeObj.index || 0));
                let node = workflow[deleteNodeObj.id];
                if (node?.detail?.controlName === ControlNameEnum.SEND_EMAIL) {
                  dispatch(removeWFEmail(node.detail.emailId));
                }
              }}
              onCancel={() => {
                if (deleteCondition) {
                  dispatch(deleteActionModal(false, "", 0, false));
                } else {
                  dispatch(deleteActionModal(false, ""));
                }
              }}
              title=""
              className={'confirm-dialog-automation'}
            />)
        }
        <AutomationDragLayer />
      </Wrapper>
    </>
  );
};

export default CreateNewAutomation;

const SpinWrapper = styled.div`
  .spin-loader {
    position: absolute;
    top: 50%;
    right: 50%;
  }
`;

const Wrapper = styled.div`
  width: 100%;
  display: block;
  position: relative;
  .disabled {
    pointer-events: none;
    opacity: 0.5;
  }
  .controll-panel {
    background-color: #ffffff;
    position: fixed;
    top: 120px;
    right: 0;
    width: 325px;
    height: calc(100vh - 120px);
    z-index: 91;
    box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.1);
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      height: calc(100vh - 90px);
      top: 90px;
      width: 280px;
    }
  }
  .confirm-dialog-automation {
    left: 48%;
  }
`;
